import React, { useState, useEffect, Fragment } from "react";
import {
    Box,
    SpeedDial,
    SpeedDialIcon,
    SpeedDialAction,
    styled,
} from "@mui/material";

import EditIconOutlined from "@mui/icons-material/EditOutlined";
import ArticleIconOutlined from "@mui/icons-material/ArticleOutlined";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import { matchPath, useLocation } from "react-router-dom";

import ContentDialog from "./ContentDialog";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
    zIndex: 999, // Ensure FAB stays on top visually
    right: 16, // Adjust position as needed
    bottom: 100, // Adjust position as needed
    display: "flex",
    position: "fixed",
    alignItems: "center",
    pointerEvents: "none", // Prevent blocking underlying elements
}));

// ----------------------------------------------------------------------

const AdminSpeedDial = () => {
    const [showModal, setShowModal] = useState(false);
    const [open, setOpen] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [dialogOperationType, setDialogOperationType] = useState("add");
    const { user } = useSelector((state) => state.auth);
    const { contentType, slug } = useSelector((state) => state.content);
    const { pathname, search } = useLocation();
    const [type, setType] = useState(null);
    const { t } = useTranslation();

    const contentMatch = matchPath("/dashboard/content/:slug/*", pathname);
    const productMatch = matchPath("/dashboard/product/:slug/*", pathname);
    const listMatch = matchPath("/dashboard/contents/:category/*", pathname);
    const isDashboardRoot = pathname === "/dashboard" || pathname === "/dashboard/";
    const routeSlug = contentMatch?.params?.slug || productMatch?.params?.slug || null;
    const listSlug =
        listMatch?.params?.category ||
        (isDashboardRoot ? "news/" : null);
    const tabParam = new URLSearchParams(search).get("tab");
    const tabIndex = Number(tabParam);
    const hasTabIndex = Number.isInteger(tabIndex) && tabIndex > 0;
    const effectiveSlug = slug || routeSlug;
    const effectiveContentType = contentType || "content";
    let creationContext = null;
    if (listSlug && hasTabIndex) {
        creationContext = {
            view: "tabbed",
            slug: listSlug,
            tabIndex,
            activeSlug:
                effectiveSlug && effectiveSlug !== listSlug
                    ? effectiveSlug
                    : null,
        };
    } else if (listSlug) {
        creationContext = { view: "list", slug: listSlug };
    } else if (routeSlug) {
        creationContext = { view: "detail", slug: routeSlug };
    }

    const genericActions = [
        {
            icon: <ArticleIconOutlined />,
            name: t("AdminSpeedDial.addContent"),
            onClick: () => {
                setDialogOperationType("add");
                setType("content");
            },
        },
        {
            icon: <AltRouteIcon />,
            name: t("AdminSpeedDial.editMenu"),
            onClick: () => {
                setDialogOperationType("add");
                setType("navigation");
            },
        },
    ];

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    useEffect(() => {
        return () => {
            setShowModal(null);
        };
    }, []);

    const getActions = () => {
        if (effectiveSlug) {
            return [
                {
                    icon: <EditIconOutlined />,
                    name: "Edit " + effectiveContentType,
                    onClick: () => {
                        setDialogOperationType("edit");
                        setType(effectiveContentType);
                    },
                },
            ].concat(genericActions);
        }

        return genericActions;
    };

    return (
        <Fragment>
            {user.is_staff === true && (
                <RootStyle>
                    <Box
                        sx={{
                            height: 320,
                            transform: "translateZ(0px)",
                            flexGrow: 1,
                        }}
                    >
                        <SpeedDial
                            ariaLabel="SpeedDial openIcon example"
                            icon={
                                <SpeedDialIcon
                                    openIcon={<EditIconOutlined />}
                                />
                            }
                            onClose={handleClose}
                            onOpen={handleOpen}
                            open={open}
                        >
                            {getActions().map((action) => (
                                <SpeedDialAction
                                    key={action.name}
                                    icon={action.icon}
                                    tooltipTitle={action.name}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClose();
                                        if (action.onClick) {
                                            action.onClick();
                                        }
                                        setShowModal(true);
                                    }}
                                />
                            ))}
                        </SpeedDial>
                        <ContentDialog
                            open={showModal && type !== null}
                            onClose={() => {
                                setShowModal(false);
                            }}
                            type={dialogOperationType}
                            contentType={type}
                            slug={effectiveSlug}
                            creationContext={creationContext}
                            submitStatus={setSubmitStatus}
                        />
                    </Box>
                </RootStyle>
            )}
        </Fragment>
    );
};

export default AdminSpeedDial;
